import { useCallback, useEffect, useRef } from 'react';

import { createCRMObjectClient } from '@/lib/crm/core/CRMObjectClient';
import type { DraftParentContext } from '@/lib/crm/core/draftEntities';
import { logger } from '@/lib/logger';
import type { CrmObject } from '@/types/crm';

import { isFieldEmpty } from './submitPayload';

// Cap at 20 contacts to match existing page_size pattern (AssociatedCrmObjectInput.tsx:175)
const MAX_AUTO_FILL_CONTACTS = 20;
const DEAL_TO_CONTACT_ASSOC = 'ahoy_deal_to_contact';
const DEAL_TO_COMPANY_ASSOC = 'ahoy_deal_to_company';
const CONTACT_TO_COMPANY_ASSOC = 'ahoy_contact_to_company';

export interface UseDealAutoFillParams {
  entityType: string;
  orgId: number;
  setFormData: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
  draftParentContext: DraftParentContext | undefined;
  /** Latest-state draft check — see CrmCreateForm's `isDraftId`. */
  isDraftId: (id: string) => boolean;
  formData: Record<string, unknown>;
}

export interface UseDealAutoFillResult {
  handleDealAutoFill: (
    associationTypeName: string,
    selectedValue: string | string[] | null,
    currentFormData: Record<string, unknown>
  ) => Promise<void>;
  /**
   * Written by the caller's mount-time init effect (for `ahoy_deal` forms)
   * with the form's initial values, so the "trigger auto-fill for initial
   * values" effect below can run the same fill logic once on mount. Owned
   * here so both effects share one ref without threading it back out through
   * component state.
   */
  pendingInitialAutoFillRef: React.RefObject<Record<string, unknown> | null>;
}

/**
 * Deal-specific auto-fill: Contact <-> Company bidirectional fill.
 *
 * Design notes:
 * - No circular loop possible: Contact selection fills Company (different assoc type),
 *   Company selection fills Contacts (different assoc type). These are one-directional.
 * - Race conditions handled via sequence number pattern with debounce.
 * - Uses Promise.allSettled for partial failure resilience.
 */
export function useDealAutoFill({
  entityType,
  orgId,
  setFormData,
  draftParentContext,
  isDraftId,
  formData,
}: UseDealAutoFillParams): UseDealAutoFillResult {
  const autoFillSequenceRef = useRef(0);
  const isMountedRef = useRef(true);
  const pendingInitialAutoFillRef = useRef<Record<string, unknown> | null>(
    null
  );

  // Track component mount state for safe async updates
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleDealAutoFill = useCallback(
    async (
      associationTypeName: string,
      selectedValue: string | string[] | null,
      currentFormData: Record<string, unknown>
    ) => {
      if (draftParentContext) {
        return;
      }
      if (entityType !== 'ahoy_deal') {
        return;
      }
      if (
        !selectedValue ||
        (Array.isArray(selectedValue) && selectedValue.length === 0)
      ) {
        return;
      }

      const sequenceNumber = ++autoFillSequenceRef.current;
      await new Promise(resolve => setTimeout(resolve, 150));

      if (autoFillSequenceRef.current !== sequenceNumber) {
        return;
      }
      if (!isMountedRef.current) {
        return;
      }

      try {
        if (associationTypeName === DEAL_TO_CONTACT_ASSOC) {
          if (!isFieldEmpty(currentFormData[DEAL_TO_COMPANY_ASSOC])) {
            return;
          }

          // Exclude draft ids: they aren't persisted server-side yet, so
          // looking them up would issue a failing request for every draft
          // selection on a top-level deal (where draftParentContext is unset).
          // Read through `isDraftId`, not this render's `draftIds`: the picker
          // reports the draft and the selection in the same click, so the
          // closure this call was bound to predates the draft landing.
          const contactIds = (
            Array.isArray(selectedValue) ? selectedValue : [selectedValue]
          ).filter(id => !isDraftId(id));
          if (contactIds.length === 0) {
            return;
          }
          const client = createCRMObjectClient(orgId, 'ahoy_contact');
          // Opt in to `associations` so the contact-to-company auto-fill
          // below can find the associated company. The backend defaults to
          // opt-out after the 2026-05-08 hotfix (see retrieve endpoint).
          const contactResults = await Promise.allSettled(
            contactIds.map(id => client.get(id, { includeAssociations: true }))
          );

          if (autoFillSequenceRef.current !== sequenceNumber) {
            return;
          }
          if (!isMountedRef.current) {
            return;
          }

          const contacts = contactResults
            .filter(
              (r): r is PromiseFulfilledResult<CrmObject> =>
                r.status === 'fulfilled'
            )
            .map(r => r.value);

          const companyIds = new Set<string>();
          for (const contact of contacts) {
            const companyAssoc = contact.associations?.find(
              a => a.association_type_name === CONTACT_TO_COMPANY_ASSOC
            );
            if (companyAssoc?.target_entity_id) {
              companyIds.add(companyAssoc.target_entity_id);
            }
          }

          if (companyIds.size === 1) {
            const companyId = Array.from(companyIds)[0];
            logger.debug('Deal auto-fill: Setting Company from Contact', {
              orgId,
              companyId,
            });
            setFormData(prev => ({
              ...prev,
              [DEAL_TO_COMPANY_ASSOC]: [companyId],
            }));
          }
        } else if (associationTypeName === DEAL_TO_COMPANY_ASSOC) {
          if (!isFieldEmpty(currentFormData[DEAL_TO_CONTACT_ASSOC])) {
            return;
          }

          // Exclude draft ids: an unsaved draft company has no server record
          // to look contacts up against, so skip it (see contact branch above).
          const companyIds = (
            Array.isArray(selectedValue) ? selectedValue : [selectedValue]
          ).filter(id => !isDraftId(id));
          if (companyIds.length === 0) {
            return;
          }
          const companyId = companyIds[0];

          const client = createCRMObjectClient(orgId, 'ahoy_contact');
          const response = await client.list({
            associated_with_entity_id: companyId,
            page_size: MAX_AUTO_FILL_CONTACTS,
            sort: 'ahoy_updated_at',
            order: 'desc',
          });

          if (autoFillSequenceRef.current !== sequenceNumber) {
            return;
          }
          if (!isMountedRef.current) {
            return;
          }

          if (response.results.length > 0) {
            const contactIds = response.results.map(
              (c: CrmObject) => c.entity_id
            );
            logger.debug('Deal auto-fill: Setting Contacts from Company', {
              orgId,
              companyId,
              contactCount: contactIds.length,
            });
            setFormData(prev => ({
              ...prev,
              [DEAL_TO_CONTACT_ASSOC]: contactIds,
            }));
          }
        }
      } catch (autoFillError) {
        logger.error(
          'Deal auto-fill failed',
          autoFillError instanceof Error ? autoFillError : undefined,
          { component: 'CrmCreateForm' }
        );
      }
    },
    [entityType, orgId, setFormData, draftParentContext, isDraftId]
  );

  // Trigger auto-fill for deal forms when initial association values are provided
  useEffect(() => {
    const initialData = pendingInitialAutoFillRef.current;
    if (!initialData) {
      return;
    }

    pendingInitialAutoFillRef.current = null;

    const contactValue = initialData[DEAL_TO_CONTACT_ASSOC];
    const companyValue = initialData[DEAL_TO_COMPANY_ASSOC];

    if (!isFieldEmpty(contactValue) && isFieldEmpty(companyValue)) {
      void handleDealAutoFill(
        DEAL_TO_CONTACT_ASSOC,
        contactValue as string | string[],
        initialData
      );
    } else if (!isFieldEmpty(companyValue) && isFieldEmpty(contactValue)) {
      void handleDealAutoFill(
        DEAL_TO_COMPANY_ASSOC,
        companyValue as string | string[],
        initialData
      );
    }
  }, [formData, handleDealAutoFill]);

  return { handleDealAutoFill, pendingInitialAutoFillRef };
}
